import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Bell,
  Save,
  AlertCircle,
  Users,
  Lock,
  Package,
  Search,
  XCircle
} from 'lucide-react';
import { AlertTypeSelector } from './AlertTypeSelector';
import { AlertEmailSelector } from './AlertEmailSelector';
import {
  Alert,
  AlertInput,
  AlertType,
  TriggerConfig,
  AlertAssignmentType,
  ProductForAlert,
  AlertProductDetail
} from '../../types/alerts';
import { useAlerts } from '../../hooks/useAlerts';
import { useAlertEmails } from '../../hooks/useAlertEmails';
import api from '../../utils/api';

interface AlertConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Para edición
  editingAlert?: Alert;
  // Para creación con productos preseleccionados
  preselectedProducts?: ProductForAlert[];
  // Para creación por SKU
  preselectedSku?: string;
  // Para creación por grupo de comparación
  preselectedGroupId?: string;
  // Tabla actual para búsqueda de productos
  currentTableName?: string;
}

export const AlertConfigModal: React.FC<AlertConfigModalProps> = ({
  isOpen,
  onClose,
  editingAlert,
  preselectedProducts = [],
  preselectedSku,
  preselectedGroupId,
  currentTableName
}) => {
  const { t } = useTranslation();
  const { createAlert, updateAlert } = useAlerts();
  const { trackMultipleEmails } = useAlertEmails();

  // Estados del formulario
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [alertType, setAlertType] = useState<AlertType | null>(null);
  const [triggerConfig, setTriggerConfig] = useState<TriggerConfig>({});
  const [assignmentType, setAssignmentType] = useState<AlertAssignmentType>('individual');
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [skuCode, setSkuCode] = useState('');
  const [comparisonGroupId, setComparisonGroupId] = useState('');
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSearchResults, setProductSearchResults] = useState<ProductForAlert[]>([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [displayProducts, setDisplayProducts] = useState<ProductForAlert[]>([]);
  const hasPreselectedProducts = preselectedProducts.length > 0;
  const currentPrice = useMemo(() => {
    const first = displayProducts[0];
    const raw = first?.sale_price;
    const num = typeof raw === 'string' ? parseFloat(raw) : raw;
    return num && !isNaN(num) ? num : undefined;
  }, [displayProducts]);

  // Estados de UI
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Inicializar valores cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      if (editingAlert) {
        // Modo edición
        setName(editingAlert.name);
        setDescription(editingAlert.description || '');
        setIsPublic(editingAlert.isPublic);
        setAlertType(editingAlert.alertType);
        setTriggerConfig(editingAlert.triggerConfig);
        setAssignmentType(editingAlert.assignmentType);
        setSelectedProductIds(editingAlert.productIds || []);
        setSkuCode(editingAlert.skuCode || '');
        setComparisonGroupId(editingAlert.comparisonGroupId || '');
        setEmailEnabled(editingAlert.emailNotification.enabled);
        setEmailRecipients(editingAlert.emailNotification.recipients);
      } else {
        // Modo creación
        setName('');
        setDescription('');
        setIsPublic(true);
        setAlertType(null);
        setTriggerConfig({});
        setEmailEnabled(false);
        setEmailRecipients([]);

        // Preselecciones
        if (preselectedProducts.length > 0) {
          setAssignmentType('individual');
          setSelectedProductIds(preselectedProducts.map(p => p.id_catalogo));
          setDisplayProducts(preselectedProducts);
        } else if (preselectedSku) {
          setAssignmentType('sku');
          setSkuCode(preselectedSku);
        } else if (preselectedGroupId) {
          setAssignmentType('comparison_group');
          setComparisonGroupId(preselectedGroupId);
        } else {
          setAssignmentType('individual');
          setSelectedProductIds([]);
        }
      }
      setError(null);
    }
  }, [isOpen, editingAlert, preselectedProducts, preselectedSku, preselectedGroupId]);

  // Prefijar precios cuando hay producto preseleccionado
  useEffect(() => {
    if (!currentPrice) return;
    setTriggerConfig(prev => {
      const next = { ...prev };
      if (next.referencePrice === undefined) {
        next.referencePrice = currentPrice;
      }
      if (next.targetValue === undefined) {
        next.targetValue = currentPrice;
      }
      return next;
    });
  }, [currentPrice]);

  // Buscar productos para referencia
  const handleSearchProduct = useCallback(async (term: string): Promise<ProductForAlert[]> => {
    if (!currentTableName) return [];

    try {
      const response = await api.get(`/tables/${currentTableName}/data`, {
        params: {
          global_search: term,
          limit: 10
        }
      });

      return (response.data.data || []).map((p: any) => ({
        id_catalogo: p.id_catalogo || p.ID_CATALOGO,
        title: p.title || p.TITLE || p.titulo,
        sitio: p.sitio || p.SITIO,
        sale_price: p.sale_price,
        imagen: p.imagen || p.IMAGEN
      }));
    } catch (err) {
      console.error('Error searching products:', err);
      return [];
    }
  }, [currentTableName]);

  // Buscar productos para asignar a la alerta
  const performProductSearch = useCallback(async () => {
    if (!currentTableName || !productSearchTerm.trim()) return;
    setProductSearchLoading(true);
    try {
      const results = await handleSearchProduct(productSearchTerm.trim());
      setProductSearchResults(results);
    } catch (err) {
      console.error('Error searching products for alert assignment:', err);
    } finally {
      setProductSearchLoading(false);
    }
  }, [currentTableName, productSearchTerm, handleSearchProduct]);

  const handleAddProduct = useCallback((product: ProductForAlert) => {
    const id = product.id_catalogo;
    if (!id) return;

    setSelectedProductIds(prev => prev.includes(id) ? prev : [...prev, id]);
    setDisplayProducts(prev => {
      if (prev.some(p => p.id_catalogo === id)) return prev;
      return [...prev, product];
    });
  }, []);

  const handleRemoveProduct = useCallback((productId: string) => {
    setSelectedProductIds(prev => prev.filter(id => id !== productId));
    setDisplayProducts(prev => prev.filter(p => p.id_catalogo !== productId));
  }, []);

  // Validar formulario
  const validateForm = (): string | null => {
    if (!alertType) {
      return t('alerts.validation.type_required');
    }

    // Validar configuración según tipo
    const typeInfo = alertType;
    if (typeInfo === 'price_greater_than' || typeInfo === 'price_less_than' || typeInfo === 'price_equals') {
      if (triggerConfig.targetValue === undefined || triggerConfig.targetValue < 0) {
        return t('alerts.validation.value_required');
      }
    }

    if (typeInfo === 'distance_value_abs' || typeInfo === 'distance_value_pct') {
      if (triggerConfig.distanceValue === undefined || triggerConfig.distanceValue < 0) {
        return t('alerts.validation.distance_required');
      }
    }

    if (typeInfo === 'price_vs_product' || typeInfo === 'distance_product_abs' || typeInfo === 'distance_product_pct') {
      if (!triggerConfig.referenceProductId) {
        return t('alerts.validation.reference_product_required');
      }
    }

    // Validar asignación
    if (assignmentType === 'individual' && selectedProductIds.length === 0) {
      return t('alerts.validation.products_required');
    }

    if (assignmentType === 'sku' && !skuCode.trim()) {
      return t('alerts.validation.sku_required');
    }

    if (assignmentType === 'comparison_group' && !comparisonGroupId) {
      return t('alerts.validation.group_required');
    }

    // Validar emails si están habilitados
    if (emailEnabled && emailRecipients.length === 0) {
      return t('alerts.validation.emails_required');
    }

    return null;
  };

  // Guardar alerta
  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const fallbackName = name.trim() || t('alerts.default_name', 'Alerta');

      // Convertir displayProducts a AlertProductDetail
      const productDetails: AlertProductDetail[] | undefined = assignmentType === 'individual' && displayProducts.length > 0
        ? displayProducts.map(p => ({
            id_catalogo: p.id_catalogo,
            title: p.title,
            sitio: p.sitio,
            imagen: p.imagen
          }))
        : undefined;

      const alertData: AlertInput = {
        name: fallbackName,
        description: description.trim() || undefined,
        isPublic,
        alertType: alertType!,
        triggerConfig,
        assignmentType,
        productIds: assignmentType === 'individual' ? selectedProductIds : undefined,
        productDetails,
        skuCode: assignmentType === 'sku' ? skuCode.trim() : undefined,
        comparisonGroupId: assignmentType === 'comparison_group' ? comparisonGroupId : undefined,
        isActive: true,
        emailNotification: {
          enabled: emailEnabled,
          recipients: emailRecipients
        }
      };

      if (editingAlert?.id) {
        await updateAlert(editingAlert.id, alertData);
      } else {
        await createAlert(alertData);
      }

      // Registrar emails usados
      if (emailEnabled && emailRecipients.length > 0) {
        await trackMultipleEmails(emailRecipients);
      }

      onClose();
    } catch (err) {
      console.error('Error saving alert:', err);
      setError(err instanceof Error ? err.message : t('alerts.error_saving'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Bell className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {editingAlert ? t('alerts.edit_alert') : t('alerts.create_alert')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Error message */}
          {error && (
            <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Layout en una columna: tarjeta de producto (si aplica) + configuración */}
          <div className="space-y-4">
            {/* Tarjeta del producto cuando viene preseleccionado desde campanita */}
            {hasPreselectedProducts && displayProducts.map(product => (
              <div key={product.id_catalogo} className="flex items-center p-3 bg-gray-50 border border-gray-200 rounded-lg">
                {product.imagen && (
                  <img src={product.imagen} alt={product.title} className="h-12 w-12 object-cover rounded mr-3" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{product.title}</p>
                  <p className="text-xs text-gray-500">{product.sitio}</p>
                  {/* Precios */}
                  {(() => {
                    const saleRaw = product.sale_price ?? triggerConfig.referencePrice;
                    const originalRaw = product.original_price;
                    const sale = saleRaw ? parseFloat(String(saleRaw)) : undefined;
                    const original = originalRaw ? parseFloat(String(originalRaw)) : undefined;
                    if (sale && original && original > sale) {
                      return (
                        <div className="text-xs text-gray-700 mt-1 space-x-2">
                          <span className="line-through text-gray-400">${original.toFixed(2)}</span>
                          <span className="font-semibold text-green-700">${sale.toFixed(2)}</span>
                        </div>
                      );
                    }
                    if (sale) {
                      return (
                        <div className="text-xs text-gray-700 mt-1">
                          <span className="font-semibold">${sale.toFixed(2)}</span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <p className="text-xs text-gray-400 mt-1">La alerta se aplicará al producto seleccionado.</p>
                </div>
              </div>
            ))}

            {/* Si no viene preseleccionado, mantener alcance + búsqueda básica */}
            {!hasPreselectedProducts && (
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setAssignmentType('individual')}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      assignmentType === 'individual'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Productos
                  </button>
                  <button
                    onClick={() => setAssignmentType('sku')}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      assignmentType === 'sku'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    SKU
                  </button>
                  <button
                    onClick={() => setAssignmentType('comparison_group')}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      assignmentType === 'comparison_group'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Grupo
                  </button>
                </div>

                {assignmentType === 'individual' && (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={productSearchTerm}
                          onChange={(e) => setProductSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="Buscar producto por nombre..."
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              performProductSearch();
                            }
                          }}
                        />
                      </div>
                      <button
                        onClick={performProductSearch}
                        disabled={productSearchLoading || !productSearchTerm.trim()}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
                      >
                        {productSearchLoading ? 'Buscando...' : 'Buscar'}
                      </button>
                    </div>

                    {productSearchResults.length > 0 && (
                      <div className="border border-gray-200 rounded-lg divide-y max-h-56 overflow-y-auto">
                        {productSearchResults.map((product) => (
                          <div key={product.id_catalogo} className="flex items-center p-2 bg-white">
                            {product.imagen && (
                              <img src={product.imagen} alt="" className="h-10 w-10 object-cover rounded mr-3" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{product.title}</p>
                              <p className="text-xs text-gray-500">{product.sitio}</p>
                            </div>
                            <button
                              onClick={() => handleAddProduct(product)}
                              className="text-sm text-blue-600 hover:text-blue-800 px-3 py-1"
                            >
                              Agregar
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {selectedProductIds.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Package className="h-4 w-4 text-gray-500" />
                          <span>{selectedProductIds.length} productos seleccionados</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {displayProducts.map(product => (
                            <span
                              key={product.id_catalogo}
                              className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                            >
                              <span className="max-w-[160px] truncate">{product.title}</span>
                              <button
                                onClick={() => handleRemoveProduct(product.id_catalogo)}
                                className="ml-2 text-gray-400 hover:text-gray-600"
                              >
                                <XCircle className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {assignmentType === 'sku' && (
                  <div className="space-y-2">
                    <label className="block text-sm text-gray-600">SKU</label>
                    <input
                      type="text"
                      value={skuCode}
                      onChange={(e) => setSkuCode(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Ej: SKU-1234"
                    />
                  </div>
                )}

                {assignmentType === 'comparison_group' && (
                  <div className="space-y-2">
                    <label className="block text-sm text-gray-600">ID de grupo de comparación</label>
                    <input
                      type="text"
                      value={comparisonGroupId}
                      onChange={(e) => setComparisonGroupId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Ingresa el ID del grupo"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Visibilidad */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-800">2. Visibilidad</h3>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setIsPublic(true)}
                  className={`flex items-center px-3 py-2 rounded-lg border ${
                    isPublic
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Users className="h-4 w-4 mr-2" />
                  {t('alerts.public')}
                </button>
                <button
                  onClick={() => setIsPublic(false)}
                  className={`flex items-center px-3 py-2 rounded-lg border ${
                    !isPublic
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Lock className="h-4 w-4 mr-2" />
                  {t('alerts.private')}
                </button>
              </div>
            </div>

            {/* Tipo y condición */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-800">3. Tipo y condición</h3>
              <AlertTypeSelector
                selectedType={alertType}
                triggerConfig={triggerConfig}
                onTypeChange={setAlertType}
                onConfigChange={setTriggerConfig}
                onSearchProduct={handleSearchProduct}
                currentPrice={currentPrice}
              />
            </div>

            {/* Notificaciones */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-800">4. Notificaciones</h3>
              <AlertEmailSelector
                selectedEmails={emailRecipients}
                onEmailsChange={setEmailRecipients}
                enabled={emailEnabled}
                onEnabledChange={setEmailEnabled}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                {t('common.saving')}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {editingAlert ? t('common.save_changes') : t('alerts.create_alert')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
